import getRuntimeConstants from "../../runtimeConstants";
import {
  addToWishlistTemplate,
  notifyMeButtonClickTemplate,
  pageLoadMixAndMatchTemplate,
  pageLoadSingleProductTemplate,
  addToCartSingleProductTemplate,
  addToCartMultipleProductTemplate,
  viewItemListTemplate,
  productComponentImpressionTemplate,
  mspOnboardingCompletedTemplate,
  mspRecommendationDisplayedTemplate,
  mspSizeSelectedTemplate,
  reviewTranslationClickedTemplate,
  reviewCoverageDisplayedTemplate,
} from "./marketingTrackingTemplates";
import { pushToDataLayer } from "../../utils/pushToDataLayer";
import getWindow from "../window";
import { getSourceRecsRequestId } from "../sourceRecommendationsRequestId";

export const fireSaveForLaterMarketing = ({
  product,
  variantsAdded,
  component_name = "pdp_hero",
  recsRequestId = undefined,
}) => {
  const sourceRecsRequestId = getSourceRecsRequestId();

  const recommendationsRequestId =
    component_name === "pdp_hero" && sourceRecsRequestId
      ? sourceRecsRequestId
      : recsRequestId;
  pushToDataLayer(
    addToWishlistTemplate({
      product,
      variantsAdded,
      component_name,
      recommendationsRequestId,
    }),
  );
};

export const fireAddToBagMarketing = ({
  products,
  variantsAddedList,
  context = "pdp_hero",
}) => {
  const { isMixAndMatch } = getRuntimeConstants();
  const recommendationsRequestId = getSourceRecsRequestId();

  if (variantsAddedList.length < 1 || !products) {
    return;
  }

  const requestId =
    context === "pdp_hero" ? getWindow()?.s?.eVar231 || null : undefined;

  if (isMixAndMatch && variantsAddedList.length > 1) {
    pushToDataLayer(
      addToCartMultipleProductTemplate({
        products,
        variantsAddedList,
        context,
        requestId,
        recommendationsRequestId,
      }),
    );
    return;
  }

  pushToDataLayer(
    addToCartSingleProductTemplate({
      products,
      variantAdded: variantsAddedList[0],
      context,
      requestId,
      recommendationsRequestId,
    }),
  );
};

export const firePageLoadMarketing = async ({ products }) => {
  const { isMixAndMatch, hostAndPath, urlParams } = getRuntimeConstants();

  if (!products) {
    return;
  }

  const requestId = getWindow()?.s?.eVar231 || null;
  const recommendationsRequestId = getSourceRecsRequestId();
  const config = {
    hostAndPath,
    urlParams,
    requestId,
    recommendationsRequestId,
  };
  if (isMixAndMatch) {
    pushToDataLayer({
      view_item: pageLoadMixAndMatchTemplate(products, config),
    });
    return;
  }

  // For single product, ensure products has variants before firing
  // Variants can be empty after filtering if they don't have prices
  if (!products.variants || !products.variants.length) {
    return;
  }

  pushToDataLayer({
    view_item: pageLoadSingleProductTemplate(products, config),
  });
};

export const fireNotifyMeButtonClickMarketing = ({
  product,
  variant,
  component_name = "pdp_hero",
  isAppDownloadNudgeShown = false,
}) => {
  pushToDataLayer(
    notifyMeButtonClickTemplate({
      product,
      variant,
      component_name,
      isAppDownloadNudgeShown,
    }),
  );
};

export const fireViewItemListMarketing = ({
  products,
  listName,
  recsRequestId,
}) => {
  if (!products || !products.length) {
    return;
  }

  const templateData = {
    products,
    listName,
  };

  if (typeof recsRequestId === "string" && recsRequestId.length > 0) {
    templateData.recsRequestId = recsRequestId;
  }

  pushToDataLayer(viewItemListTemplate(templateData));
};

export const fireProductComponentImpression = ({
  products,
  itemListName,
  row = undefined,
}) => {
  if (!products || !products.length) {
    return;
  }

  pushToDataLayer(
    productComponentImpressionTemplate({
      products,
      itemListName,
      row,
    }),
  );
};

const buildSponsoredYmalPayload = ({ products, listName }) => {
  if (!products || !products.length) {
    return null;
  }

  const sponsoredProducts = products.filter((product) => product.advertisement);

  if (!sponsoredProducts.length) {
    return null;
  }

  const adPositions = [];
  products.forEach((product, index) => {
    if (product.advertisement) {
      adPositions.push(index);
    }
  });

  const adProducts = sponsoredProducts.map(
    (product) =>
      product.advertisement?.ProductName ||
      product.productTitle ||
      product.name,
  );

  const payload = viewItemListTemplate({
    products,
    listName,
    includeSponsoredMetadata: true,
  });

  payload.view_item_list.has_sponsored_ads = true;
  payload.view_item_list.ad_positions = adPositions;
  payload.view_item_list.ad_products = adProducts;
  payload.view_item_list.ad_count = adProducts.length;

  return payload;
};

export const fireSponsoredYmalLoadMarketing = ({
  products,
  listName = "pdp_ymal",
}) => {
  const payload = buildSponsoredYmalPayload({ products, listName });

  if (!payload) {
    return;
  }

  pushToDataLayer(payload);
};

export const fireSponsoredYmalInViewMarketing = ({
  products,
  listName = "pdp_ymal",
}) => {
  fireSponsoredYmalLoadMarketing({ products, listName });
};

export const fireMspOnboardingCompleted = ({ product }) => {
  pushToDataLayer(mspOnboardingCompletedTemplate({ product }));
};

export const fireMspRecommendationDisplayed = ({
  product,
  recommendedSizeCount,
}) => {
  pushToDataLayer(
    mspRecommendationDisplayedTemplate({ product, recommendedSizeCount }),
  );
};
export const fireMspSizeSelected = ({ product, isRecommended }) => {
  pushToDataLayer(mspSizeSelectedTemplate({ product, isRecommended }));
};

export const fireReviewTranslationClicked = ({
  product,
  reviewId,
  sourceLanguage,
  targetLanguage,
}) => {
  pushToDataLayer(
    reviewTranslationClickedTemplate({
      product,
      reviewId,
      sourceLanguage,
      targetLanguage,
    }),
  );
};

export const fireReviewCoverageDisplayed = ({
  product,
  totalReviewCount,
  locale,
}) => {
  const hasMinimumCoverage = totalReviewCount >= 3;
  pushToDataLayer(
    reviewCoverageDisplayedTemplate({
      product,
      totalReviewCount,
      hasMinimumCoverage,
      locale,
    }),
  );
};
